import React, { useState, useEffect, useRef, Fragment } from 'react'

import { EditorState } from 'draft-js'
import { Editor } from "react-draft-wysiwyg"
import BaseMorphed from './BaseMorphed'
import { Input, Grid, Popup, Button, Icon, Dropdown, Label, Checkbox } from 'semantic-ui-react'
import { useDispatch, useSelector } from 'react-redux'
import { builderActions } from '../../../actions/builderActions'
import { useTranslation } from 'react-i18next'
import { paymentTypes } from '../../../constants'
import ErrorLabel from '../../partials/ErrorLabel'
import { getError } from '../../../utils/helper'
import i18next from 'i18next'
import IntlCurrencyInput from '../../../utils/IntlCurrencyInput'
import { noticeService } from '../../../api'

export default function PaymentMorphed({item}) {

    // ID For Open projects
    const openType = 'open'

    // ID For Itemised Payments
    const itemisedType = paymentTypes.itemised.value
    const {t} = useTranslation()
    const community = useSelector(state => state.navigation.community)

    const isFinanceIntegration = community?.integrations_sams_projects_enabled ?? false

    const [types] = useState([
        {
            key: paymentTypes.open.id,
            value: paymentTypes.open.value,
            text: paymentTypes.open.name
        },
        {
            key: paymentTypes.fixed.id,
            value: paymentTypes.fixed.value,
            text: paymentTypes.fixed.name
        },
        {
            key: paymentTypes.itemised.id,
            value: paymentTypes.itemised.value,
            text: paymentTypes.itemised.name
        },
        {
            key: paymentTypes.partial.id,
            value: paymentTypes.partial.value,
            text: paymentTypes.partial.name
        },
    ])

    const [data, setData] = useState({
        type: item.data ? item.data.type : null,
        title: item.data ? item.data.title : "",
        amount: item.data ? item.data.amount : 0,
        floatAmount: item.data ? item.data.floatAmount : 0,
        showQuantity: item.data?.showQuantity ?? false,
        maxQuantityLimit: item.data ? item.data.maxQuantityLimit : '',
        currency: item.data ? item.data.currency : community.payment_currency_iso_4217,
        originalId: item?.data?.originalId,
        projectName: item.data?.projectName ?? '',
    })

    // Tracks whether Project Name is still live-mirroring the title.
    // Starts linked for new components; detaches permanently on manual edit.
    const [isProjectNameLinked, setIsProjectNameLinked] = useState(!item.data?.projectName)
    const [projectNameBlurred, setProjectNameBlurred] = useState(false)
    const [projectNameChecking, setProjectNameChecking] = useState(false)
    const [projectNameAvailability, setProjectNameAvailability] = useState(null)

    const dispatch = useDispatch()
    const inputRef = useRef()
    const dropdownRef = useRef()
    const projectNameDebounceRef = useRef(null)

    useEffect(() => {
        dispatch(builderActions.saveTemplateData({
            item,
            data: {
                ...data,
                maxQuantityLimit: data.showQuantity ? data.maxQuantityLimit : '',
                hasFinanceIntegration: isFinanceIntegration,
            }
        }))
        return () => {}
    }, [data, dispatch, isFinanceIntegration])

    const handleTypeChange = (e, d) => {
        dropdownRef.current.close()
        // We will clear the amount when an open type is selected
        if (d.value === openType) {
            setData({...data, type: d.value, amount: 0, floatAmount: 0})
            return;
        }
        setData({...data, type: d.value})
    }

    const handleChangeTitle = (e, d) => {
        const newTitle = d.value
        setData(prev => ({
            ...prev,
            title: newTitle,
            ...(isFinanceIntegration && isProjectNameLinked ? { projectName: newTitle } : {})
        }))
    }

    const handleChangeAmount = (e, floatValue, maskedValue, minorUnits) => {
        setData({item, ...data, amount: minorUnits, floatAmount: floatValue})
    }

    const handleCheckChange = (e, d) => {
        setData((prev) => ({
            ...prev, showQuantity: !prev.showQuantity,
            maxQuantityLimit: !prev.showQuantity ? '' : prev.maxQuantityLimit
        }))
    }

    const handleQuantityChange = (e, d) => {
        setData((prev) => ({...prev, maxQuantityLimit: d.value}))
    }

    const checkProjectNameAvailability = (name) => {
        if (!name.trim()) {
            setProjectNameAvailability(null)
            return
        }
        setProjectNameChecking(true)
        const payload = {
            community_id: community.id,
            name,
        }

        if (item.data?.originalId) {
            payload.payment_component_id = item.data.originalId
        }
        
        noticeService.checkSamsProjectName(payload)
            .then(res => {
                setProjectNameAvailability(res.data)
            })
            .catch(() => setProjectNameAvailability(null))
            .finally(() => setProjectNameChecking(false))
    }

    const handleChangeProjectName = (e, d) => {
        setIsProjectNameLinked(false)
        setProjectNameAvailability(null)
        setData(prev => ({...prev, projectName: d.value}))

        clearTimeout(projectNameDebounceRef.current)
        projectNameDebounceRef.current = setTimeout(() => {
            checkProjectNameAvailability(d.value)
        }, 500)
    }

    const handleProjectNameBlur = () => {
        setProjectNameBlurred(true)
        clearTimeout(projectNameDebounceRef.current)
        if (data.projectName) {
            checkProjectNameAvailability(data.projectName)
        }
    }

    const amountLabel = data.type === itemisedType ? t('messageBuilder.morphedTemplates.PaymentMorphed.amountPerItem') : t('messageBuilder.morphedTemplates.PaymentMorphed.amount')
    
    return (
        <BaseMorphed 
            item={item} 
            infoOnDelete={{
                content: t("messageBuilder.morphedTemplates.PaymentMorphed.deleteInfo")
            }}
            render={({errors}) => (
                <Grid>
                    {(data.type === paymentTypes.partial.value) && (
                        <Grid.Row>
                            <Grid.Column>
                                <div className="partial-payments-note">
                                    {t("messageBuilder.morphedTemplates.PaymentMorphed.types.partialDeadlineRecommended")}
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    <Grid.Row>
                        <Grid.Column>
                            <div className="input-container theme-color">
                                <label className='label'>{t('messageBuilder.morphedTemplates.PaymentMorphed.paymentItemTitle')}</label>
                                <Input autoComplete="off" value={data.title} onChange={handleChangeTitle} placeholder={t('messageBuilder.morphedTemplates.PaymentMorphed.paymentItemTitle')} />
                            </div>
                            <ErrorLabel error={getError("title", errors)} />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>
                            <div className="input-container theme-color">
                                <label className='label'>
                                    {t('messageBuilder.morphedTemplates.PaymentMorphed.paymentType')}
                                    <Popup
                                        trigger={<Icon className="info circle info-icon morphed in-label primary-text"/>}
                                        content={(
                                            <div>
                                                <p className="no-margin">{t('messageBuilder.morphedTemplates.PaymentMorphed.paymentTypeBreakdown')}</p>
                                                <ul>
                                                    <li><strong>{paymentTypes.fixed.name}:</strong> {t('messageBuilder.morphedTemplates.PaymentMorphed.types.fixedDescription')}</li>
                                                    <li><strong>{paymentTypes.itemised.name}:</strong> {t('messageBuilder.morphedTemplates.PaymentMorphed.types.itemisedDescription')}</li>
                                                    <li><strong>{paymentTypes.open.name}:</strong> {t('messageBuilder.morphedTemplates.PaymentMorphed.types.openDescription')}</li>
                                                    <li><strong>{paymentTypes.partial.name}:</strong> {t('messageBuilder.morphedTemplates.PaymentMorphed.types.partialDescription')}</li>
                                                </ul>
                                            </div>
                                        )}
                                    />
                                </label>
                                <Dropdown 
                                    ref={dropdownRef}
                                    placeholder={t('messageBuilder.morphedTemplates.PaymentMorphed.paymentType')}
                                    fluid 
                                    selection
                                    onChange={handleTypeChange}
                                    value={data.type} 
                                    options={types}
                                    className='payment-component-types' />
                            </div>
                            <ErrorLabel error={getError("type", errors)} />
                        </Grid.Column>
                    </Grid.Row>
                    
                    {/* Hide Amount for Open Types */}
                    {(data.type !== openType && community != null) && (
                        <Grid.Row>
                            <Grid.Column>
                                <div className="input-container theme-color disabled-on-edit">
                                    <label className='label'>{amountLabel}</label>
                                    <div className="ui input">
                                        <IntlCurrencyInput
                                            currencyOverride={community.payment_currency_iso_4217 === 'ZAR' ? {"ZAR": "R"} : null}
                                            currency={community.payment_currency_iso_4217}
                                            onChange={handleChangeAmount}
                                            value={data.floatAmount}
                                            config={{
                                                locale: i18next.language,
                                                formats: {
                                                    number: {
                                                        [community.payment_currency_iso_4217]: {
                                                            currency: community.payment_currency_iso_4217,
                                                            style: "currency",
                                                        }
                                                    }
                                                }
                                            }} />
                                    </div>
                                </div>
                                <ErrorLabel error={getError("amount", errors)} />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    
                    {/* Hide Amount for Open Types */}
                    {(data.type === paymentTypes.itemised.value) && (
                        <Fragment>
                            <Grid.Row>
                                <Grid.Column>
                                    <div className='vertical-align disabled-on-edit'>
                                        <Checkbox
                                            checked={data.showQuantity}
                                            label={t('messageBuilder.morphedTemplates.PaymentMorphed.quantity.limit')}
                                            onChange={handleCheckChange}/>
                                        <Popup
                                            basic
                                            size="small"
                                            trigger={
                                                <Icon className="info circle info-icon community-roles" />
                                            }
                                            content={t('messageBuilder.morphedTemplates.PaymentMorphed.quantity.limit.popup')}
                                        />
                                    </div>
                                </Grid.Column>
                            </Grid.Row>
                            {data.showQuantity && (
                                <Grid.Row>
                                    <Grid.Column>
                                        <div className="input-container theme-color disabled-on-edit">
                                            <label className='label'>{t('messageBuilder.morphedTemplates.PaymentMorphed.quantity.limit.input')}</label>
                                            <Input
                                                autoComplete="off"
                                                value={data.maxQuantityLimit}
                                                onChange={handleQuantityChange}
                                                type='number'
                                                min={data.maxQuantityLimit}
                                                max={data.maxQuantityLimit}
                                                placeholder={t('messageBuilder.morphedTemplates.PaymentMorphed.quantity.limit.input')} />
                                        </div>
                                        <ErrorLabel error={getError("maxQuantityLimit", errors)} />
                                    </Grid.Column>
                                </Grid.Row>
                            )}
                        </Fragment>
                    )}

                    {isFinanceIntegration && (
                        <Grid.Row>
                            <Grid.Column>
                                <div className="input-container theme-color">
                                    <label className='label'>
                                        {t('messageBuilder.morphedTemplates.PaymentMorphed.projectName')}
                                        <span style={{color: 'red'}}> *</span>
                                    </label>
                                    <Input
                                        autoComplete="off"
                                        value={data.projectName}
                                        onChange={handleChangeProjectName}
                                        onBlur={handleProjectNameBlur}
                                        placeholder={t('messageBuilder.morphedTemplates.PaymentMorphed.projectName')}
                                        loading={projectNameChecking}
                                    />
                                </div>
                                <p className="label grey-text small-text">{t('messageBuilder.morphedTemplates.PaymentMorphed.projectNameHelper')}</p>
                                {projectNameBlurred && !data.projectName && (
                                    <ErrorLabel error={{ isValid: false, message: t('requestObjects.PostCreateNoticeRequest.payment.projectNameRequired') }} />
                                )}
                                {projectNameAvailability && !projectNameAvailability.available && (
                                    <ErrorLabel error={{ isValid: false, message: projectNameAvailability.message }} />
                                )}
                                <ErrorLabel error={getError("projectName", errors)} />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                </Grid>
            )} />
    )
}
